// src/components/WorkTimeStatisticsModal.js 
import React, { useState, useEffect } from "react";
import "./WorkTimeStatisticsModal.css";
import { API_URL } from "../config";

const WorkTimeStatisticsModal = ({
  onClose,
  option,
  statsData,
  loadingStats,
  statsError,
}) => {
  // Stany dla opcji "Czas Pracy Pracownika"
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeStats, setEmployeeStats] = useState(null);
  const [loadingEmployeeStats, setLoadingEmployeeStats] = useState(false);
  const [employeeStatsError, setEmployeeStatsError] = useState("");

  // Stany dla opcji "Czas Pracy Zlecenia"
  const [activeOrders, setActiveOrders] = useState([]);
  const [closedOrders, setClosedOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderStats, setOrderStats] = useState(null);
  const [loadingOrderStats, setLoadingOrderStats] = useState(false);
  const [orderStatsError, setOrderStatsError] = useState("");

  // Deklarujemy stany dla accordionów (używane tylko przy opcji "Czas Pracy Zlecenia")
  const [activeExpanded, setActiveExpanded] = useState(true);
  const [closedExpanded, setClosedExpanded] = useState(false);

  // Dla "Czas Pracy Pracownika" – pobieramy listę pracowników, gdy opcja jest wybrana
  useEffect(() => {
    if (option === "Czas Pracy Pracownika") {
      fetch(`${API_URL}/orders/workers`, {
        credentials: "include",
      })
        .then((res) => res.json())
        .then((data) => setEmployees(data))
        .catch((err) => console.error("Błąd pobierania pracowników:", err));
    }
  }, [option]);

  // Dla "Czas Pracy Zlecenia" – pobieramy listę zleceń (aktywne i zamknięte)
  useEffect(() => {
    if (option === "Czas Pracy Zlecenia") {
      // Aktywne zlecenia (stan_archiwizacji = false)
      fetch(`${API_URL}/orders?archiwum=false`, {
        credentials: "include",
      })
        .then((res) => res.json())
        .then((data) => setActiveOrders(data))
        .catch((err) => console.error("Błąd pobierania aktywnych zleceń:", err));
      // Zamknięte zlecenia (stan_archiwizacji = true)
      fetch(`${API_URL}/orders?archiwum=true`, {
        credentials: "include",
      })
        .then((res) => res.json())
        .then((data) => setClosedOrders(data))
        .catch((err) => console.error("Błąd pobierania zamkniętych zleceń:", err));
    }
  }, [option]);

  // Funkcja pobierająca statystyki dla wybranego pracownika
  const fetchEmployeeStats = async (employeeId) => {
    setLoadingEmployeeStats(true);
    setEmployeeStatsError("");
    try {
      const res = await fetch(
        `${API_URL}/working_time/statistics/employee/${employeeId}`,
        { credentials: "include" }
      );
      if (!res.ok)
        throw new Error("Błąd pobierania statystyk pracownika.");
      const data = await res.json();
      setEmployeeStats(data);
    } catch (err) {
      setEmployeeStatsError(err.message);
    } finally {
      setLoadingEmployeeStats(false);
    }
  };

  // Funkcja pobierająca statystyki dla wybranego zlecenia
  const fetchOrderStats = async (orderId) => {
    setLoadingOrderStats(true);
    setOrderStatsError("");
    try {
      const res = await fetch(
        `${API_URL}/working_time/statistics/order/${orderId}`,
        { credentials: "include" }
      );
      if (!res.ok)
        throw new Error("Błąd pobierania statystyk zlecenia.");
      const data = await res.json();
      setOrderStats(data);
    } catch (err) {
      setOrderStatsError(err.message);
    } finally {
      setLoadingOrderStats(false);
    }
  };

  // Funkcja renderująca zawartość modalu na podstawie opcji
  const renderContent = () => {
    if (option === "Mój Czas Pracy") {
      return (
        <div className="stats-content">
          {loadingStats ? (
            <p>Ładowanie statystyk...</p>
          ) : statsError ? (
            <p style={{ color: "red" }}>{statsError}</p>
          ) : statsData ? (
            <div>
              <p>
                <strong>Łączny czas pracy:</strong> {statsData.total} godzin
              </p>
              <h4>Aktualne zlecenia:</h4>
              {statsData.activeOrders.length > 0 ? (
                <ul>
                  {statsData.activeOrders.map((order) => (
                    <li key={order.nr_ref}>
                      {order.nr_ref}: {order.hours_per_order} godzin
                    </li>
                  ))}
                </ul>
              ) : (
                <p>Brak aktywnych zleceń</p>
              )}
              <h4>Zamknięte zlecenia:</h4>
              {statsData.closedOrders.length > 0 ? (
                <ul>
                  {statsData.closedOrders.map((order) => (
                    <li key={order.nr_ref}>
                      {order.nr_ref}: {order.hours_per_order} godzin
                    </li>
                  ))}
                </ul>
              ) : (
                <p>Brak zamkniętych zleceń</p>
              )}
            </div>
          ) : (
            <p>Brak danych.</p>
          )}
        </div>
      );
    } else if (option === "Czas Pracy Pracownika") {
      return (
        <div className="stats-two-columns">
          <div className="stats-left">
            <h4>Wybierz pracownika:</h4>
            <ul className="employee-list">
              {employees.map((emp) => (
                <li
                  key={emp.id}
                  className={
                    selectedEmployee && selectedEmployee.id === emp.id
                      ? "selected"
                      : ""
                  }
                  onClick={() => {
                    setSelectedEmployee(emp);
                    fetchEmployeeStats(emp.id);
                  }}
                >
                  {emp.first_name} {emp.last_name}
                </li>
              ))}
            </ul>
          </div>
          <div className="stats-right">
            {loadingEmployeeStats ? (
              <p>Ładowanie statystyk pracownika...</p>
            ) : employeeStatsError ? (
              <p style={{ color: "red" }}>{employeeStatsError}</p>
            ) : employeeStats ? (
              <div>
                <p>
                  <strong>
                    {selectedEmployee.first_name} {selectedEmployee.last_name}
                  </strong>
                </p>
                <p>
                  <strong>Łączny czas pracy:</strong> {employeeStats.total} godzin
                </p>
                <h5>Aktualne zlecenia:</h5>
                {employeeStats.activeOrders.length > 0 ? (
                  <ul>
                    {employeeStats.activeOrders.map((order) => (
                      <li key={order.nr_ref}>
                        {order.nr_ref}: {order.hours_per_order} godzin
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>Brak aktywnych zleceń</p>
                )}
                <h5>Zamknięte zlecenia:</h5>
                {employeeStats.closedOrders.length > 0 ? (
                  <ul>
                    {employeeStats.closedOrders.map((order) => (
                      <li key={order.nr_ref}>
                        {order.nr_ref}: {order.hours_per_order} godzin
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>Brak zamkniętych zleceń</p>
                )}
              </div>
            ) : (
              <p>Wybierz pracownika, aby zobaczyć statystyki.</p>
            )}
          </div>
        </div>
      );
    } else if (option === "Czas Pracy Zlecenia") {
      return (
        <div className="stats-two-columns">
          <div className="stats-left">
            <div className="accordion-section">
              <h4
                className="accordion-header"
                onClick={() => setActiveExpanded(!activeExpanded)}
              >
                Aktywne zlecenia
              </h4>
              {activeExpanded && (
                <ul className="order-list scrollable">
                  {activeOrders.length > 0 ? (
                    activeOrders.map((order) => (
                      <li
                        key={order.id}
                        className={
                          selectedOrder && selectedOrder.id === order.id
                            ? "selected"
                            : ""
                        }
                        onClick={() => {
                          setSelectedOrder(order);
                          fetchOrderStats(order.id);
                        }}
                      >
                        {order.nr_ref}
                      </li>
                    ))
                  ) : (
                    <p>Brak aktywnych zleceń</p>
                  )}
                </ul>
              )}
            </div>
            <div className="accordion-section">
              <h4
                className="accordion-header"
                onClick={() => setClosedExpanded(!closedExpanded)}
              >
                Zamknięte zlecenia
              </h4>
              {closedExpanded && (
                <ul className="order-list scrollable">
                  {closedOrders.length > 0 ? (
                    closedOrders.map((order) => (
                      <li
                        key={order.id}
                        className={
                          selectedOrder && selectedOrder.id === order.id
                            ? "selected"
                            : ""
                        }
                        onClick={() => {
                          setSelectedOrder(order);
                          fetchOrderStats(order.id);
                        }}
                      >
                        {order.nr_ref}
                      </li>
                    ))
                  ) : (
                    <p>Brak zamkniętych zleceń</p>
                  )}
                </ul>
              )}
            </div>
          </div>
          <div className="stats-right">
            {loadingOrderStats ? (
              <p>Ładowanie statystyk zlecenia...</p>
            ) : orderStatsError ? (
              <p style={{ color: "red" }}>{orderStatsError}</p>
            ) : orderStats && selectedOrder ? (
              <div>
                <p>
                  <strong>Zlecenie:</strong> {selectedOrder.nr_ref}{" "}
                  {selectedOrder.tytul ? selectedOrder.tytul : ""}
                </p>
                <p>
                  <strong>Łączny czas pracy:</strong>{" "}
                  {orderStats.employees
                    ? orderStats.employees.reduce(
                        (sum, emp) => sum + Number(emp.hours_worked),
                        0
                      )
                    : 0}{" "}
                  godzin
                </p>
                <h5>Pracownicy:</h5>
                {orderStats.employees && orderStats.employees.length > 0 ? (
                  <ul>
                    {orderStats.employees.map((emp) => (
                      <li key={emp.id}>
                        {emp.first_name} {emp.last_name}: {emp.hours_worked} godzin
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>Brak danych</p>
                )}
              </div>
            ) : (
              <p>Wybierz zlecenie, aby zobaczyć statystyki.</p>
            )}
          </div>
        </div>
      );
    } else {
      return (
        <div className="stats-content">
          <p>Wybierz opcję statystyk z menu.</p>
        </div>
      );
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{option}</h3>
          <button className="modal-close-btn" onClick={onClose}>
            &times;
          </button>
        </div>
        <div className="modal-body">{renderContent()}</div>
      </div>
    </div>
  );
};

export default WorkTimeStatisticsModal;
